/* Minimal Gamers — a single hardware overview and live GPO configurator.
 * Original controls stay inside their original GPO group and product form.
 * This layer does not change prices, input values, required flags or cart payloads. */
(() => {
  'use strict';
  const mounted = new WeakSet();
  const order = ['cpu', 'gpu', 'board', 'memory', 'storage', 'cooler', 'case', 'psu'];
  const labels = {cpu:'Processore',gpu:'Scheda video',board:'Scheda madre',memory:'Memoria RAM',storage:'SSD principale',cooler:'Dissipatore',case:'Case',psu:'Alimentatore'};
  const text = el => (el?.textContent || '').replace(/\s+/g, ' ').trim();
  const setText = (el, value) => { if (el && el.textContent !== value) el.textContent = value; };
  const el = (tag, cls, content) => {
    const node = document.createElement(tag); if (cls) node.className = cls;
    if (content !== undefined) node.textContent = content; return node;
  };
  function groupKey(group) {
    const name = text(group.querySelector('.mg-gpo-summary__label, .gpo-label')).toUpperCase();
    if (/PROCESSORE|^CPU\b/.test(name)) return 'cpu';
    if (/SCHEDA (?:VIDEO|GRAFICA)|^GPU\b/.test(name)) return 'gpu';
    if (/SCHEDA MADRE/.test(name)) return 'board';
    if (/MEMORIA RAM/.test(name)) return 'memory';
    if (/SSD.*PRINCIPALE/.test(name)) return 'storage';
    if (/DISSIPATORE|RAFFREDDAMENTO/.test(name)) return 'cooler';
    if (/^CASE\b/.test(name)) return 'case';
    if (/ALIMENTATORE/.test(name)) return 'psu';
    return '';
  }
  function selectedValues(group) {
    const checked = [...group.querySelectorAll('input[type="radio"]:checked,input[type="checkbox"]:checked')];
    if (checked.length) return checked.map(input => input.value).filter(Boolean);
    const select = group.querySelector('select');
    return select?.value ? [text(select.selectedOptions[0])] : [];
  }
  function options(group) {
    return [...group.querySelectorAll('input[type="radio"],input[type="checkbox"]')].map(input => {
      const label = group.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      return {input, label, image:label?.querySelector('.mg-gpo-option-image img'), value:input.value};
    });
  }
  function mount(root) {
    if (mounted.has(root) || root.dataset.mgConfigurator === 'true') return;
    const machine = root.querySelector('.mg-rex-machine');
    const rail = machine?.querySelector('.mg-rex-machine__rail');
    const config = root.querySelector('#personalizza-rex');
    const slot = config?.querySelector('[data-mg-gpo-slot]');
    const form = root.querySelector('[data-mg-rex-form]');
    if (!machine || !rail || !config || !slot || !form || !form.contains(machine) || !form.contains(config)) return;
    mounted.add(root);
    let initialized = false, scheduled = false, activeDialog = null, fixedFingerprint = '', groups = [];
    let extraExpanded = true, inView = false;
    const dialogs = new WeakMap();
    let layout, extrasToggle, mobileBar, status, total, monthly, submit;
    const name = () => text(root.querySelector('[data-mg-profile-name]')) || 'PC GAMING';
    const schedule = () => { if (!scheduled) {scheduled = true; requestAnimationFrame(() => {scheduled = false; sync();});} };
    function available(group) {
      return !group.hidden && group.style.display !== 'none' && !group.classList.contains('gpo-hidden');
    }
    function openFirstMissing() {
      const missing=groups.find(group=>available(group)&&group.dataset.mgRequired==='true'&&!selectedValues(group).length);
      if(!missing)return false;
      if(!missing.classList.contains('is-mg-open'))missing.querySelector('.mg-gpo-summary')?.click();
      missing.scrollIntoView({behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',block:'center'});
      return true;
    }
    function advance(event) {
      event?.stopPropagation();
      if(!openFirstMissing())submit?.click();
    }
    function addFixedCards(app) {
      const editable = new Set(groups.filter(available).map(groupKey).filter(Boolean));
      const source = [...rail.querySelectorAll('[data-mg-spec-index]')].map((card, index) => {
        const key = card.dataset.mgSpecKey || ['cpu','gpu','board','memory','cooler','storage','psu'][index];
        return {key:key === 'ram' ? 'memory' : key === 'ssd' ? 'storage' : key,
          title:text(card.querySelector('[data-mg-spec-label]')),
          image:card.querySelector('img')?.getAttribute('src') || '',
          detail:text(card.querySelector('[data-mg-spec-detail]'))};
      }).filter(item => order.includes(item.key) && !editable.has(item.key) && item.title);
      if(!editable.has('case')&&!source.some(item=>item.key==='case')){
        // Names verified against the current Shopify descriptions, not inferred from an illustration.
        const fixedCases={'DEEPCOOL CRYO':'DEEPCOOL CL6600 HYPERSPLIT','DEEPCOOL GLACIER':'DEEPCOOL CL6600 WHITE HYPERSPLIT','DEEPCOOL MONOLITH':'DEEPCOOL CG590U','DEEPCOOL AURORA':'DEEPCOOL CH560 DIGITAL WHITE'};
        source.push({key:'case',title:fixedCases[name()]||'Case incluso nella build '+name(),image:window.MGUnifiedMedia?.fixedCases?.[name()]||root.querySelector('.mg-rex-hero img')?.getAttribute('src')||'',detail:'Case della configurazione, non modificabile'});
      }
      const integratedGraphics=source.find(item=>item.key==='gpu'&&/VEGA|RADEON\s+(?:740M|780M)|INTEGRAT/i.test(item.title));
      if(integratedGraphics){integratedGraphics.image=rail.querySelector('[data-mg-spec-key="cpu"] img,[data-mg-spec-index="0"] img')?.getAttribute('src')||'';integratedGraphics.detail='Grafica integrata nel processore';}
      const fingerprint = JSON.stringify(source);
      if (fingerprint === fixedFingerprint) return;
      fixedFingerprint = fingerprint;
      app.querySelectorAll('.mg-u-fixed').forEach(card => card.remove());
      source.forEach(item => {
        const card = el('article','mg-u-fixed'); card.dataset.mgUnifiedKey = item.key;
        card.style.order = String(order.indexOf(item.key));
        card.append(el('h3','mg-u-card-label',labels[item.key]),el('span','mg-u-fixed-badge','Incluso · componente fisso'));
        const image = el('img','mg-u-fixed-image'); image.width=180; image.height=130;
        image.loading='lazy'; image.decoding='async'; image.alt=item.title;
        const matchedImage=window.MGUnifiedMedia?.resolve(item.title,item.key,app.dataset.optionSetId);
        if (matchedImage||item.image) image.src=matchedImage||item.image;
        else image.hidden=true;
        const title = el('strong','mg-u-fixed-title',item.title);
        const note = el('span','mg-u-card-foot',item.detail==='Grafica integrata nel processore'?item.detail:'Già incluso · Non modificabile');
        card.append(image,title,note); app.append(card);
      });
    }
    function initialize(app) {
      initialized = true;
      root.classList.add('mg-u-pdp'); machine.classList.add('mg-u-machine'); config.classList.add('mg-u-config');
      const shell = machine.querySelector('.mg-rex-shell');
      shell.append(config); // Move the original form section; never clone app controls.
      layout = el('div','mg-u-layout');
      const main = el('div','mg-u-main');
      const purchase = el('div','mg-u-purchase');
      purchase.setAttribute('aria-label','Totale e acquisto della configurazione');
      const guidance = el('div','mg-u-guidance');
      guidance.append(el('b','','PERSONALIZZA IL TUO PC'),el('span','','Premi CAMBIA per vedere le alternative e i relativi prezzi.'),el('p','mg-u-required-help','Conferma il componente incluso oppure scegli un upgrade.'));
      main.append(slot);
      const originalHeading = config.querySelector('.mg-rex-section-heading');
      status = originalHeading?.querySelector('.mg-rex-config-status');
      total = originalHeading?.querySelector('[data-mg-config-total]');
      monthly = originalHeading?.querySelector('[data-mg-config-monthly]');
      const pricePanel = originalHeading?.querySelector('.mg-rex-config-total');
      if (status) {status.setAttribute('aria-live','polite');guidance.append(status);}
      if (pricePanel) {
        const label = pricePanel.querySelector('small');
        if (label) label.textContent = 'TOTALE DELLA TUA BUILD';
        purchase.append(pricePanel);
      }
      const footer = config.querySelector('.mg-rex-config__footer');
      submit = footer?.querySelector('[data-mg-primary-submit]');
      if(submit){
        // GPO retains its original submit control. The visible guide is not an app ATC selector.
        submit.hidden=true;
        const guideButton=el('button','mg-u-add');guideButton.type='button';
        guideButton.append(el('span','','AGGIUNGI AL CARRELLO'),el('span','','→'));
        guideButton.addEventListener('click',advance);footer.append(guideButton);
      }
      if (footer) purchase.append(footer);
      layout.append(main); config.append(purchase,guidance,layout);
      extrasToggle = el('button','mg-u-extras-toggle'); extrasToggle.type='button';
      extrasToggle.setAttribute('aria-expanded','true');
      extrasToggle.append(el('span','','EXTRA E SERVIZI OPZIONALI'),el('b','mg-u-extras-count','−'));
      extrasToggle.addEventListener('click',() => {extraExpanded=!extraExpanded; syncExtras();});
      app.append(extrasToggle);
      const note = el('p','mg-u-image-note','Foto brandizzate illustrative. Modello, caratteristiche e prezzo sono quelli indicati nella scelta.');
      main.append(note);
      const mobile = el('div','mg-u-mobile-bar');
      const mobileCopy = el('div','mg-u-mobile-price'); mobileCopy.append(el('small','','TOTALE CONFIGURAZIONE'),el('strong',''));
      const mobileButton = el('button','mg-u-mobile-atc','Continua'); mobileButton.type='button';
      mobileButton.addEventListener('click',advance);
      mobile.append(mobileCopy,mobileButton); mobile.hidden=true; mobileBar=mobile; root.append(mobile);
      new IntersectionObserver(entries => {inView=entries.some(entry=>entry.isIntersecting);syncMobile();},{threshold:0}).observe(machine);
      root.addEventListener('click',event => {
        if(!event.target.closest('[data-mg-add-proxy],[data-mg-primary-submit]'))return;
        if(!groups.some(group=>available(group)&&group.dataset.mgRequired==='true'&&!selectedValues(group).length))return;
        event.preventDefault();event.stopImmediatePropagation();
        openFirstMissing();
      },true);
      root.addEventListener('change',() => {schedule();window.setTimeout(schedule,100);});
      root.addEventListener('mg:product-profile',() => {fixedFingerprint='';schedule();window.setTimeout(schedule,150);});
    }
    function syncExtras() {
      config.classList.toggle('mg-u-extras-open',extraExpanded);
      extrasToggle.setAttribute('aria-expanded',String(extraExpanded));
      const extras=groups.filter(group=>group.dataset.mgUnifiedExtra==='true'&&available(group));
      const selected=extras.reduce((sum,group)=>sum+selectedValues(group).length,0);
      setText(extrasToggle.querySelector('b'),(selected ? `${selected} selezionati · ` : '')+(extraExpanded?'−':'+'));
      extrasToggle.hidden=!extras.length;
    }
    function syncMobile() {
      if (!mobileBar) return;
      mobileBar.hidden=!inView||!!activeDialog;
      root.classList.toggle('mg-u-bar-visible',inView&&!activeDialog);
      document.body.classList.toggle('mg-u-bar-active',inView&&!activeDialog);
      setText(mobileBar.querySelector('strong'),text(total));
      const missing=groups.filter(group=>available(group)&&group.dataset.mgRequired==='true'&&!selectedValues(group).length).length;
      const button=mobileBar.querySelector('button');
      setText(button,missing?'Completa le scelte →':'Aggiungi al carrello →');
      button.disabled=!!submit?.disabled;
      const guideButton=config.querySelector('.mg-u-add');if(guideButton)guideButton.disabled=!!submit?.disabled;
    }
    function close(group, restore=true) {
      const dialog=dialogs.get(group); if (!dialog) return;
      group.classList.remove('is-mg-open');
      group.querySelector('.mg-gpo-summary')?.setAttribute('aria-expanded','false');
      if (dialog.open) dialog.close();
      if (activeDialog===dialog) activeDialog=null;
      document.documentElement.classList.remove('mg-u-modal-open');
      if (restore) group.querySelector('.mg-gpo-summary')?.focus({preventScroll:true});
      syncMobile();
    }
    function decorateDialog(group) {
      if (dialogs.has(group)) return;
      const controls=group.querySelector('.mg-gpo-real-control');
      const trigger=group.querySelector('.mg-gpo-summary');
      if (!controls||!trigger||typeof HTMLDialogElement==='undefined') return;
      const dialog=el('dialog','mg-u-dialog');
      dialog.id=`mg-u-options-${root.id||'product'}-${group.dataset.mgGroupIndex}`;
      const header=el('header','mg-u-dialog-header');
      const heading=el('h3','',text(group.querySelector('.mg-gpo-summary__label')));
      heading.id=dialog.id+'-title'; dialog.setAttribute('aria-labelledby',heading.id);
      const copy=el('div',''); copy.append(el('small','','PERSONALIZZA '+name()),heading);
      const closer=el('button','mg-u-dialog-close','×'); closer.type='button'; closer.setAttribute('aria-label','Chiudi le opzioni');
      closer.addEventListener('click',()=>close(group)); header.append(copy,closer);
      const footer=el('footer','mg-u-dialog-footer');
      const done=el('button','mg-u-dialog-done','Fatto · torna alla build');done.type='button';done.addEventListener('click',()=>close(group));
      footer.append(el('span','','Il totale si aggiorna con le tue scelte.'),done);
      dialog.append(header,controls,footer);group.append(dialog);dialogs.set(group,dialog);
      trigger.setAttribute('aria-haspopup','dialog');trigger.setAttribute('aria-controls',dialog.id);
      dialog.addEventListener('cancel',event=>{event.preventDefault();close(group);});
      dialog.addEventListener('click',event=>{
        if(event.target!==dialog)return;
        const rect=dialog.getBoundingClientRect();
        if(event.clientX<rect.left||event.clientX>rect.right||event.clientY<rect.top||event.clientY>rect.bottom)close(group);
      });
      dialog.addEventListener('close',()=>{
        if(group.classList.contains('is-mg-open'))close(group);
      });
      new MutationObserver(()=>{
        if(group.classList.contains('is-mg-open')) {
          if(activeDialog&&activeDialog!==dialog)close(activeDialog.closest('.gpo-element'),false);
          if(!dialog.open){
            const extra=group.dataset.mgUnifiedExtra==='true';
            if(extra&&!extraExpanded){extraExpanded=true;syncExtras();}
            dialog.showModal();activeDialog=dialog;
            document.documentElement.classList.add('mg-u-modal-open');
            controls.querySelectorAll('img[data-src]').forEach(image=>{if(image.src!==image.dataset.src)image.src=image.dataset.src;});
            closer.focus({preventScroll:true});syncMobile();
          }
        } else if(dialog.open)close(group);
      }).observe(group,{attributes:true,attributeFilter:['class']});
      if(group.classList.contains('is-mg-open'))group.classList.remove('is-mg-open');
    }
    function sync() {
      if(!root.isConnected)return;
      const app=slot.querySelector('.gpo-app.mg-rex-gpo');
      groups=[...slot.querySelectorAll('.gpo-element[data-mg-decorated="true"]')];
      // Keep the original page until the real option set has mounted successfully.
      if(!app||!groups.length)return;
      if(!initialized)initialize(app);
      addFixedCards(app);
      groups.forEach((group,index)=>{
        const key=groupKey(group),extra=!key&&group.dataset.mgRequired!=='true';
        if(key&&group.dataset.mgUnifiedKey!==key)group.dataset.mgUnifiedKey=key;
        options(group).forEach(option=>{
          const url=window.MGUnifiedMedia?.resolve(option.value,group.dataset.mgGroupType);
          if(!url)return;
          if(option.input.dataset.mgOptionImage!==url)option.input.dataset.mgOptionImage=url;
          if(option.image&&option.image.dataset.src!==url){option.image.dataset.src=url;option.image.src=url;}
        });
        if(group.dataset.mgUnifiedExtra!==String(extra))group.dataset.mgUnifiedExtra=String(extra);
        const groupOrder=String(key?order.indexOf(key):extra?200+index:100+index);
        if(group.style.order!==groupOrder)group.style.order=groupOrder;
        const trigger=group.querySelector('.mg-gpo-summary');
        const selected=selectedValues(group);
        let hint=trigger?.querySelector('.mg-u-action');
        if(trigger&&!hint){hint=el('span','mg-u-action');trigger.append(hint);}
        const actionNames={cpu:'PROCESSORE',gpu:'SCHEDA VIDEO',board:'SCHEDA MADRE',memory:'RAM',storage:'SSD',cooler:'DISSIPATORE',case:'CASE',psu:'ALIMENTATORE'};
        setText(hint,extra?'AGGIUNGI EXTRA +':'CAMBIA '+(actionNames[key]||'COMPONENTE')+' →');
        const triggerLabel=`${extra?'Aggiungi extra: ':'Cambia '+(actionNames[key]||'componente')+': '}${labels[key]||text(group.querySelector('.mg-gpo-summary__label'))}. ${selected.length?selected.join(', '):'scegli il componente'}`;
        if(trigger&&trigger.getAttribute('aria-label')!==triggerLabel)trigger.setAttribute('aria-label',triggerLabel);
        decorateDialog(group);
      });
      const guide=machine.querySelector('[data-mg-profile-machine-guide]');
      machine.querySelector('.mg-rex-machine__kicker')?.remove();
      setText(machine.querySelector('.mg-rex-machine__heading h2>span'),'CONFIGURA IL TUO');
      setText(machine.querySelector('[data-mg-profile-machine-title]'),name()+'.');
      setText(guide,'LA TUA BUILD. I TUOI UPGRADE. TUTTO QUI.');
      setText(machine.querySelector('[data-mg-profile-machine-copy]'),'Controlla i componenti inclusi e scegli qui le personalizzazioni. Il totale si aggiorna in tempo reale.');
      syncExtras();syncMobile();
    }
    new MutationObserver(records=>{
      if(records.some(record=>record.type==='childList'||record.target===submit))schedule();
    }).observe(root,{childList:true,subtree:true,attributes:true,attributeFilter:['disabled']});
    schedule();
  }
  const start=scope=>{scope.querySelectorAll?.('[data-mg-rex-pdp]').forEach(mount);if(scope.matches?.('[data-mg-rex-pdp]'))mount(scope);};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>start(document),{once:true});else start(document);
  document.addEventListener('shopify:section:load',event=>start(event.target));
})();
